import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Minimal auth slice — holds the controlled login form state and the
 * loading flag the LoginForm reads via `useAppSelector(state => state.auth)`.
 *
 * Tokens / user / tenant context are NOT stored here — those live in the
 * encrypted Redis session. This slice is purely UI state.
 */
export interface AuthState {
  email: string;
  password: string;
  isLoading: boolean;
}

/**
 * Dev convenience: prefill the login form from `.env.local` so the same
 * credentials don't have to be typed on every reload.
 *
 * The `NODE_ENV` guard is what keeps this out of production — `NEXT_PUBLIC_*`
 * values are inlined into the client bundle at build time, so without it a
 * prod build would ship whatever is in the env. The vars are absent from
 * `.env.example` on purpose; set them locally only.
 */
const isDev = process.env.NODE_ENV === "development";

const initialState: AuthState = {
  email: isDev ? (process.env.NEXT_PUBLIC_DEV_LOGIN_EMAIL ?? "") : "",
  password: isDev ? (process.env.NEXT_PUBLIC_DEV_LOGIN_PASSWORD ?? "") : "",
  isLoading: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setEmail(state, action: PayloadAction<string>) {
      state.email = action.payload;
    },
    setPassword(state, action: PayloadAction<string>) {
      state.password = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    clearAuthForm(state) {
      state.email = "";
      state.password = "";
      state.isLoading = false;
    },
  },
});

export const { setEmail, setPassword, setIsLoading, clearAuthForm } =
  authSlice.actions;
export default authSlice.reducer;
